/**
 * Money.
 *
 * Everything is stored and calculated as an integer number of PAISE. Rupees only
 * exist at the edges — what a user types in, and what we print back out.
 *
 * The reason is that 0.1 + 0.2 !== 0.3 in binary floating point. In a system
 * that sums thousands of payments across a multi-crore project, those errors
 * accumulate into figures that do not reconcile — and an ERP whose totals are a
 * rupee out is an ERP nobody trusts.
 *
 *   ₹85,00,000  ->  850000000 paise
 *   ₹392.50     ->  39250 paise
 */

/** A whole number of paise. Never a float, never a string. */
export type Paise = number;

export class MoneyError extends Error {}

const MAX_PAISE = Number.MAX_SAFE_INTEGER; // ≈ ₹90,07,19,92,54,740 — far beyond any real project

/** Rupees (as typed by a user) to paise. Rounds to the nearest paisa. */
export function toPaise(rupees: number | string): Paise {
  const n = typeof rupees === 'string' ? parseRupees(rupees) : rupees;
  if (!Number.isFinite(n)) throw new MoneyError(`Not a number: ${rupees}`);
  const paise = Math.round(n * 100);
  if (!Number.isSafeInteger(paise)) throw new MoneyError(`Amount out of range: ${rupees}`);
  return paise;
}

/** Paise to rupees as a number. Only for display or export — never for maths. */
export function toRupees(paise: Paise): number {
  assertPaise(paise);
  return paise / 100;
}

/**
 * Parses what a person actually types: "85,00,000", "₹1,20,000.50", "1200".
 * Returns rupees as a number. Throws on anything it cannot read, rather than
 * silently returning NaN and writing a broken row.
 */
export function parseRupees(input: string): number {
  const cleaned = input
    .replace(/[₹\s,_]/g, '')
    .replace(/^\+/, '')
    .trim();
  if (cleaned === '' || cleaned === '-') throw new MoneyError('Empty amount');
  if (!/^-?\d*\.?\d*$/.test(cleaned)) throw new MoneyError(`Cannot read amount: "${input}"`);
  const n = Number(cleaned);
  if (!Number.isFinite(n)) throw new MoneyError(`Cannot read amount: "${input}"`);
  return n;
}

/**
 * Indian digit grouping: last three digits, then pairs.
 *   850000000 paise -> "85,00,000"
 * Deliberately returns digits only; the caller adds the symbol so this can be
 * reused for Excel exports where a bare number is wanted.
 */
export function formatIndianDigits(paise: Paise, opts: { paise?: boolean } = {}): string {
  assertPaise(paise);
  const negative = paise < 0;
  const abs = Math.abs(paise);

  const whole = Math.trunc(abs / 100);
  const fraction = abs % 100;

  const s = String(whole);
  let grouped: string;
  if (s.length <= 3) {
    grouped = s;
  } else {
    const last3 = s.slice(-3);
    const rest = s.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',');
    grouped = `${rest},${last3}`;
  }

  const showPaise = opts.paise ?? fraction !== 0;
  const body = showPaise ? `${grouped}.${String(fraction).padStart(2, '0')}` : grouped;
  return negative ? `-${body}` : body;
}

/** "₹85,00,000". Use this everywhere money is shown to a user. */
export function formatMoney(paise: Paise | null | undefined, opts: { paise?: boolean } = {}): string {
  if (paise === null || paise === undefined) return '—';
  return `₹${formatIndianDigits(paise, opts)}`;
}

/**
 * Short form for dashboards and tight table cells.
 *   850000000  -> "₹85.0 L"
 *   4200000000 -> "₹4.2 Cr"
 */
export function formatMoneyShort(paise: Paise | null | undefined): string {
  if (paise === null || paise === undefined) return '—';
  assertPaise(paise);
  const rupees = Math.abs(paise) / 100;
  const sign = paise < 0 ? '-' : '';
  if (rupees >= 1_00_00_000) return `${sign}₹${trim(rupees / 1_00_00_000)} Cr`;
  if (rupees >= 1_00_000) return `${sign}₹${trim(rupees / 1_00_000)} L`;
  if (rupees >= 1_000) return `${sign}₹${trim(rupees / 1_000)} K`;
  return formatMoney(paise);
}

function trim(n: number): string {
  return n.toFixed(n < 10 ? 1 : 0).replace(/\.0$/, '');
}

/** Sum that stays in integers. */
export function sumPaise(values: readonly (Paise | null | undefined)[]): Paise {
  let total = 0;
  for (const v of values) {
    if (v === null || v === undefined) continue;
    assertPaise(v);
    total += v;
    if (!Number.isSafeInteger(total)) throw new MoneyError('Sum out of range');
  }
  return total;
}

/**
 * quantity × rate, both of which may be fractional, rounded to whole paise.
 * 2.4 tonnes at ₹66,500 is ₹1,59,600 — and must be exactly that, not
 * 159599.99999999997.
 */
export function lineAmount(quantity: number, ratePaise: Paise): Paise {
  if (!Number.isFinite(quantity)) throw new MoneyError(`Bad quantity: ${quantity}`);
  assertPaise(ratePaise);
  const amount = Math.round(quantity * ratePaise);
  if (!Number.isSafeInteger(amount)) throw new MoneyError('Line amount out of range');
  return amount;
}

/** Never negative — a balance of "-0" reads as an error to a user. */
export function balance(totalPaise: Paise, paidPaise: Paise): Paise {
  return Math.max(0, totalPaise - paidPaise);
}

function assertPaise(v: unknown): asserts v is Paise {
  if (typeof v !== 'number' || !Number.isInteger(v)) {
    throw new MoneyError(`Money must be an integer number of paise, got: ${String(v)}`);
  }
  if (Math.abs(v) > MAX_PAISE) throw new MoneyError('Amount out of range');
}
